import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Divider,
} from '@mui/material';
import {
  ErrorBox,
  ErrorTitle,
  ErrorMessage,
  Link,
} from '@marageti/z4-lib';
import PasswordLogin from '../../components/auth/email-password-form';
import { ReactComponent as ZicassoSVG } from '../../assets/svg/zicasso.svg';
import { ViewProps } from './types';
import './login.scss';
import config from '../../config';
import GoogleLoginView from '../../components/auth/google-sso-button';

const View = ({
  loginError = undefined,
  onSubmit,
  email,
  isFromTripRequest,
}: ViewProps): React.ReactElement => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      mx: '20px',
    }}
  >
    <Stack
      spacing={3}
      sx={{
        maxWidth: '333px',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Stack flexDirection="column" spacing="16px" alignItems="center">
        <ZicassoSVG aria-label="Zicasso" />
        {!isFromTripRequest && (
          <Typography color="text.secondary">
            Please sign in to continue.
          </Typography>
        )}
        {isFromTripRequest && (
        <>
          <Typography
            component="h1"
            variant="h2"
            color="text.primary"
            sx={{
              textAlign: 'center',
              fontWeight: '700',
            }}
          >
            Thank you for submitting
            <br />
            your trip request!
          </Typography>
          <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
            Our team is reviewing your trip request to match you with top travel specialists. You will hear from them shortly.
          </Typography>
        </>
        )}
      </Stack>
      <GoogleLoginView onSuccess={onSubmit} />
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Divider sx={{ flex: 1 }} />
        <Typography variant="body2" color="" margin="0 12px">or</Typography>
        <Divider sx={{ flex: 1 }} />
      </Box>

      <PasswordLogin
        onSubmit={onSubmit}
        disablePasswordValidation
        defaultValues={{ username: email || '', password: '' }}
        emailReadOnly={isFromTripRequest && !!email}
        error={
          loginError && (
            <ErrorBox data-e2e="ztest-login-error">
              <ErrorTitle>
                {loginError.code === 401 ? 'You have entered an incorrect email or password.' : 'There was an error logging in.'}
              </ErrorTitle>
              {loginError.code && loginError.code !== 401 && (
                <ErrorMessage>
                  {`Code: ${loginError.code} `}
                  {loginError.message || 'Please contact support'}
                  {loginError.id && <br />}
                  {loginError.id && `ID: ${loginError.id}`}
                </ErrorMessage>
              )}
            </ErrorBox>
          )
        }
      />

      <Stack
        spacing={2}
        textAlign="center"
      >
        <Link
          color="primary"
          sx={{ '&:hover': { color: 'secondary.main' } }}
          to="/reset"
          underline="always"
          variant="subtitle1"
        >
          Forgot Password?
        </Link>
        <Typography variant="body2">
          Travel Specialists
          {' '}
          <Link
            color="primary"
            sx={{ '&:hover': { color: 'secondary.main' } }}
            to={config.PARTNERS_URL}
            underline="always"
          >
            Sign In Here
          </Link>
        </Typography>
      </Stack>
    </Stack>
  </Box>
);

export default View;
